import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTourStore } from "@/store/tour";

interface TourChallengeProps {
  /** Helper name that returns boolean when challenge is completed */
  challengeCheck: string;
  onComplete: () => void;
}

const POLL_INTERVAL = 500;
const TIMEOUT_MS = 30_000;

export function TourChallenge({ challengeCheck, onComplete }: TourChallengeProps) {
  const { t } = useTranslation();
  const helpers = useTourStore((s) => s.helpers);
  const [completed, setCompleted] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setCompleted(false);
    setTimedOut(false);

    // Poll the challenge check helper
    pollRef.current = setInterval(() => {
      const checker = helpers[challengeCheck];
      if (checker && checker()) {
        setCompleted(true);
        if (pollRef.current) clearInterval(pollRef.current);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }
    }, POLL_INTERVAL);

    // Timeout after 30s
    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, TIMEOUT_MS);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [challengeCheck, helpers]);

  // Auto-advance after success animation
  useEffect(() => {
    if (completed) {
      const timer = setTimeout(onComplete, 1000);
      return () => clearTimeout(timer);
    }
  }, [completed, onComplete]);

  if (completed) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-100 rounded-lg animate-pulse">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {t("tour.challengeSuccess")}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-amber-600 font-medium animate-pulse">
        {t("tour.tryChallenge")}
      </span>
      {timedOut && (
        <button
          onClick={onComplete}
          className="px-2 py-1 text-[10px] text-gray-500 hover:text-gray-700 underline"
        >
          {t("tour.challengeTimeout")}
        </button>
      )}
    </div>
  );
}
